import {
  createContext,
  useContext,
  useEffect,
  useState,
  type PropsWithChildren
} from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { createStore, useStore, type StoreApi } from "zustand";

import { useCurrentSupplier } from "~/api/suppliers/hooks/use-supplier";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { type Till } from "~/api/tills/types";
import { type User } from "~/api/users/types";
import { INACTIVE_WAITTIME_DURATION_MS } from "~/components/kiosk/ui/modals/constants";
import { OrderMinimumBlockModal } from "~/components/kiosk/ui/modals/order-minimum/order-minimum-block-modal";
import { ScreenLoaderLogo } from "~/components/screen/screen-loader-logo";
import { useInactive } from "~/hooks/use-inactive";
import { useKioskTheme } from "~/hooks/use-kiosk-theme";
import { logger } from "~/services/logger";
import {
  useKioskSession,
  useKioskSessionStatus
} from "~/stores/kiosk-session/hooks/use-kiosk-session";
import { useUser } from "~/api/users/hooks/use-user";

import { KioskScannerListener } from "./kiosk-scanner-listener";

type KioskStore = {
  till: Extract<Till, { type: "kiosk" }>;
  logoUrl: string | null | undefined;
  user: User | undefined;
};

const KioskStoreContext = createContext<StoreApi<KioskStore> | null>(null);

export const KioskLayoutPage = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { data: till, status: tillStatus } = useCurrentTill();
  const { data: supplier, status: supplierStatus } = useCurrentSupplier();

  const childId = useKioskSession((session) => session?.childId ?? null);
  const { data: user } = useUser({ childId });

  const kioskSessionStatus = useKioskSessionStatus();

  useKioskTheme({
    theme:
      till?.type === "kiosk" ? till.config?.temporaryBrandingColors : undefined
  });

  useInactive({
    inactiveTimeout: INACTIVE_WAITTIME_DURATION_MS,
    disabled:
      pathname.includes("/inactive") ||
      pathname.includes("/payment") ||
      pathname.includes("/freedompay") ||
      !["/kiosk/products", "/kiosk/basket"].some((route) =>
        pathname.includes(route)
      ),
    onInactive: () => {
      // Normalize the path to ensure consistent handling
      const normalizedPath = pathname.endsWith("/")
        ? pathname.slice(0, -1)
        : pathname;
      navigate(`${normalizedPath}/inactive`);
    }
  });

  if (pathname === "/kiosk/error") {
    return <Outlet />;
  }

  if (tillStatus === "pending" || supplierStatus === "pending") {
    return <ScreenLoaderLogo />;
  }

  if (till?.type !== "kiosk") {
    logger.warn("Till is not a kiosk", { till_id: till?.id });
    return <Navigate to="/profiles" />;
  }

  if (
    pathname !== "/kiosk" &&
    pathname !== "/kiosk/admin" &&
    pathname !== "/kiosk/products/admin" &&
    kioskSessionStatus !== "active"
  ) {
    return <Navigate to="/kiosk" replace />;
  }

  return (
    <KioskStoreProvider till={till} logoUrl={supplier?.logo?.url} user={user}>
      <Outlet />

      <KioskScannerListener />
      <OrderMinimumBlockModal />
    </KioskStoreProvider>
  );
};

const KioskStoreProvider = ({
  till,
  logoUrl,
  user,
  children
}: PropsWithChildren<KioskStore>) => {
  // eslint-disable-next-line react/hook-use-state -- useState for one time initialization (https://tkdodo.eu/blog/use-state-for-one-time-initializations)
  const [store] = useState(() =>
    createStore(() => ({
      till,
      logoUrl,
      user
    }))
  );

  // Update the user in the store when it changes
  useEffect(() => {
    store.setState({ user });
  }, [user, store]);

  return (
    <KioskStoreContext.Provider value={store}>
      {children}
    </KioskStoreContext.Provider>
  );
};

export const useKioskStore = <T,>(selector: (state: KioskStore) => T) => {
  const store = useContext(KioskStoreContext);
  if (!store) {
    throw new Error("Missing KioskStoreProvider");
  }
  return useStore(store, selector);
};
